import React, { useCallback, useLayoutEffect, useMemo } from 'react';
import type { ViewProps } from 'react-native';
import {
  BodyText,
  checkIfRecaptchaEnabled,
  Form,
  TEST_GRECAPTCHA,
  TextLink,
  useNoticeBannersStackContext,
  useReCaptcha,
} from '@sweetgreen/garnish';

import { useIsLoggedIn } from '@order/AuthMachine';
import { PageWrapper } from '@order/components';
import { useCustomer } from '@order/Customer';
import { useCart } from '@order/hooks';
import type { TranslationKeys } from '@order/Localization';
import { useLocalizationContext } from '@order/Localization';
import { useTrackEffect } from '@order/Telemetry';
import { formatBirthdayForSubmit, getMonthNames } from '@order/utils';

import { useRedirectAfterAuthStateChange } from '../../navigation';
import type { JoinScreenProps } from '../../navigation/AppNavigation.props';
import type { JoinFormData } from './JoinScreen.hooks';
import { useJoinForm } from './JoinScreen.hooks';
import { TermsAndConditions } from './subcomponents';

// ────────────────────────────────────────────────────────────────────────────────

export const JoinScreen = ({ navigation, route }: JoinScreenProps) => {
  const {
    redirect = 'home',
    redirectToPlanId,
    redirectToCampaignId,
  } = route.params ?? {};

  const { signUp } = useCustomer();
  const isLoggedIn = useIsLoggedIn();
  const { isCartEmpty } = useCart();

  const redirectAfterAuthStateChange = useRedirectAfterAuthStateChange();

  // ─────────────────────────────────────────────────────────────────

  const join = useCallback(
    async (input: JoinInput) => {
      const { email } = input;

      const response = await signUp(input);
      const shouldVerifyNewUser = response?.__typename === 'RegisterSuccess';

      if (shouldVerifyNewUser)
        navigation.replace('JoinVerification', {
          email,
          redirect,
          redirectToPlanId,
          redirectToCampaignId,
        });

      return response;
    },
    [navigation, redirect, redirectToPlanId, redirectToCampaignId, signUp],
  );

  const handlePressSignIn = useCallback(() => {
    navigation.navigate('Login', {
      redirect,
      redirectToPlanId,
      redirectToCampaignId,
    });
  }, [navigation, redirect, redirectToPlanId, redirectToCampaignId]);

  // ─────────────────────────────────────────────────────────────────

  useLayoutEffect(() => {
    if (isLoggedIn) redirectAfterAuthStateChange();
  }, [isLoggedIn, redirectAfterAuthStateChange]);

  // ─── Telemetry ──────────────────────────────────────────────────────────────────────

  useTrackEffect('join.view', {
    entryPoint: isCartEmpty ? 'login' : 'checkout',
  });

  // ────────────────────────────────────────────────────────────────────

  return (
    <JoinForm
      testID="join-form"
      join={join}
      onPressSignIn={handlePressSignIn}
    />
  );
};

// ────────────────────────────────────────────────────────────────────────────────

export const JoinForm = ({ testID, join, onPressSignIn }: JoinFormProps) => {
  const { isSigningUp } = useCustomer();
  const { t } = useLocalizationContext();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();

  // ─── ReCaptcha ───────────────────────────────────────────────────

  const { reCaptchaMachine, reCaptchaRef } = useReCaptcha();

  const [reCaptchaState] = reCaptchaMachine;
  const isLoadingRecaptcha = reCaptchaState.matches('loading');
  const isExecutingRecaptcha = reCaptchaState.matches('executing');

  // ────────────────────────────────────────────────────────────────────

  const { form } = useJoinForm();
  const { control, handleSubmit, formState } = form;
  const { errors, isSubmitting } = formState;

  // ─── Helpers ────────────────────────────────────────────────────────

  const signUpUser = useCallback(
    async (signUpFormData: JoinFormData) => {
      const {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        terms,
        birthday,
        grecaptcha = TEST_GRECAPTCHA,
        ...formData
      } = signUpFormData;

      const formattedBirthday = formatBirthdayForSubmit(birthday);

      await join({
        ...formData,
        ...(formattedBirthday ? { birthday: formattedBirthday } : {}),
        grecaptcha,
      });

      // reset recaptcha after request
      reCaptchaRef.current?.resetRecaptcha();
    },
    [join, reCaptchaRef],
  );

  const onRecaptchaVerify = useCallback(
    async (grecaptcha: string) => {
      if (!grecaptcha) return;

      const formValues = form.getValues();

      await signUpUser({ ...formValues, grecaptcha });
    },
    [form, signUpUser],
  );

  const onRecaptchaError = useCallback(() => {
    addNoticeBanner({ text: t('general.error'), palette: 'caution' });
  }, [addNoticeBanner, t]);

  const submitForm = useMemo(() => {
    const isRecaptchaEnabled = checkIfRecaptchaEnabled();

    return handleSubmit(async (formData) => {
      await (isRecaptchaEnabled
        ? reCaptchaRef.current?.executeRecaptcha()
        : signUpUser(formData));
    });
  }, [handleSubmit, reCaptchaRef, signUpUser]);

  // ────────────────────────────────────────────────────────────────────

  const monthNames = getMonthNames().map((key) => t(key as TranslationKeys));

  // ────────────────────────────────────────────────────────────────────

  const shouldShowLoadingSubmitBtn =
    isSubmitting || isSigningUp || isExecutingRecaptcha;
  const shouldDisableSubmitBtn = isLoadingRecaptcha;

  // ─────────────────────────────────────────────────────────────────

  return (
    <PageWrapper>
      <Form.Container
        testID={testID}
        reCaptchaRef={reCaptchaRef}
        reCaptchaMachine={reCaptchaMachine}
        onRecaptchaVerify={onRecaptchaVerify}
        onRecaptchaError={onRecaptchaError}
      >
        <Form.Title>{t('join.title')}</Form.Title>

        <Form.Subtitle direction="row">
          <BodyText size={3}>{t('join.subtitle')} </BodyText>
          <TextLink onPress={onPressSignIn} testID="link-login">
            {t('login.title')}
          </TextLink>
        </Form.Subtitle>

        {/* ========== first name ========== */}

        <Form.Group>
          <Form.TextField
            name="firstName"
            control={control}
            rules={{ required: true }}
            testID="field-firstName"
            label={t('form.first-name')}
            hideSubmitButton
            onSubmit={submitForm}
            autoComplete="name-given"
            autoCapitalize="words"
            notice={errors.firstName?.message}
            disabled={isSigningUp}
          />
        </Form.Group>

        {/* ========== last name ========== */}

        <Form.Group>
          <Form.TextField
            name="lastName"
            control={control}
            rules={{ required: true }}
            testID="field-lastName"
            label={t('form.last-name')}
            hideSubmitButton
            onSubmit={submitForm}
            autoComplete="name-family"
            autoCapitalize="words"
            notice={errors.lastName?.message}
            disabled={isSigningUp}
          />
        </Form.Group>

        {/* ========== email ========== */}

        <Form.Group>
          <Form.TextField
            name="email"
            control={control}
            rules={{ required: true }}
            keyboardType="email-address"
            testID="field-email"
            label={t('form.email')}
            hideSubmitButton
            onSubmit={submitForm}
            autoComplete="email"
            notice={errors.email?.message}
            disabled={isSigningUp}
          />
        </Form.Group>

        {/* ========== phone number ========== */}

        <Form.Group>
          <Form.TextFieldPhoneNumber
            name="phoneNumber"
            control={control}
            rules={{ required: true }}
            testID="field-phoneNumber"
            label={t('form.phone-number')}
            maxLength={10}
            hideSubmitButton
            onSubmit={submitForm}
            notice={errors.phoneNumber?.message}
            disabled={isSigningUp}
            helperText={t('join.phone-number-helper-text')}
          />
        </Form.Group>

        {/* ========== password ========== */}

        <Form.Group>
          <Form.TextFieldPassword
            name="password"
            control={control}
            rules={{ required: true }}
            testID="field-password"
            label={t('form.password')}
            hideSubmitButton
            onSubmit={submitForm}
            autoComplete="password-new"
            notice={errors.password?.message}
            disabled={isSigningUp}
            toggleTestID="field-password-toggle"
          />
        </Form.Group>

        {/* ========== birthday ========== */}

        <Form.Group>
          <Form.BirthdayPicker
            name="birthday"
            control={control}
            label={t('form.birthday')}
            accessibilityLabel={t('form.birthday')}
            monthLabel={t('general.month')}
            dayLabel={t('general.day')}
            helperText={t('join.birthday-helper-text')}
            monthNames={monthNames}
          />
        </Form.Group>

        {/* ========== terms and conditions ========== */}

        <TermsAndConditions
          control={control}
          errorMessage={errors.terms?.message}
        />

        {/* ========== ========== ========== */}

        <Form.SubmitButton
          testID="button-sign-up"
          isLoading={shouldShowLoadingSubmitBtn}
          disabled={shouldDisableSubmitBtn}
          onPress={submitForm}
        >
          {t('join.create-account')}
        </Form.SubmitButton>
      </Form.Container>
    </PageWrapper>
  );
};

// ─── TYPES ──────────────────────────────────────────────────────────────────────

type JoinFormProps = Readonly<{
  join: Join;
  onPressSignIn: () => void;
}> &
  Pick<ViewProps, 'testID'>;

type JoinInput = Parameters<Join>[0];

type Join = ReturnType<typeof useCustomer>['signUp'];
